import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Table, Select } from 'antd';
import type { TableColumnsType } from 'antd';
import { SearchIcon, CaretIcon, RefreshIcon } from '../../../assets/icons';
import Pagination from '../../../components/Pagination';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { PLATFORM_TABLE_THEME } from './tableTheme';
import { BIND_BLOCK, DEPARTMENTS, isOwn, type Binding } from './mockData';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetStateDepartmentsQuery,
  useUpdateStateDepartmentMutation,
  type StateDepartmentRow,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — Departments (prototype `isList`).
 * The departments enabled for the state: each is a library template plus
 * that state's edits only. Open one to change its structure, map its
 * territories, record its people and see its minister.
 * ------------------------------------------------------------------ */

const LEGEND: { label: string; cls: string }[] = [
  { label: 'Anchor: same as a level of LGD, SEC, ECI or another hierarchy', cls: BIND_BLOCK.anchor },
  { label: 'Inherited from an anchor above', cls: BIND_BLOCK.inherited },
  { label: 'Own: each unit is made of units of a chosen level', cls: BIND_BLOCK.grouped },
  { label: 'Own · listed: places or offices, listed one by one', cls: BIND_BLOCK.facility },
];

const covColor = (c: number) => (c >= 100 ? 'text-brand' : c >= 70 ? 'text-[#8a4b0f]' : 'text-[#b42318]');

/** The client-only Party hierarchy's level bindings (no template in the catalog). */
const PARTY_BINDS: Binding[] = ['anchor', 'grouped', 'anchor', 'grouped', 'booth', 'inherited'];

/** Level bindings for a department row, resolved from the template catalog. */
const bindsOf = (code: string): Binding[] => {
  if (code === 'party') return PARTY_BINDS;
  const t = DEPARTMENTS.find((d) => d.code === code);
  return t ? t.levels.map((l) => l.bind) : [];
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const { code: stateCode, name: stateName } = usePlatformState();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'state' | 'client'>('all');
  const [status, setStatus] = useState<'all' | 'ready' | 'progress' | 'draft'>('all');
  const [sort, setSort] = useState<'catalog' | 'work'>('catalog');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data = [], isLoading } = useGetStateDepartmentsQuery(stateCode);
  const [updateStateDepartment] = useUpdateStateDepartmentMutation();

  const open = (code: string) => navigate(`/admin/departments/${code}`);

  // Derived status key per row, used by both the Status cell and the filter.
  const statusKey = (d: StateDepartmentRow): 'ready' | 'progress' | 'draft' =>
    d.code === 'party' ? 'draft' : d.coverage == null ? 'ready' : 'progress';

  const rows: StateDepartmentRow[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    let r = data.filter((d) => {
      if (q && !(d.name.toLowerCase().includes(q) || d.code.toLowerCase().includes(q))) return false;
      if (scope === 'client' && d.code !== 'party') return false;
      if (scope === 'state' && d.code === 'party') return false;
      if (status !== 'all' && statusKey(d) !== status) return false;
      return true;
    });
    if (sort === 'work') {
      r = [...r].sort((a, b) => {
        // coverage ascending with nulls LAST (nulls/'Ready' are fully mapped)
        if (a.coverage == null && b.coverage == null) return 0;
        if (a.coverage == null) return 1;
        if (b.coverage == null) return -1;
        return a.coverage - b.coverage;
      });
    }
    return r;
  }, [data, search, scope, status, sort]);

  const resetFilters = () => {
    setSearch('');
    setScope('all');
    setStatus('all');
    setSort('catalog');
    setPage(1);
  };
  const onFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paged = rows.slice((page - 1) * pageSize, page * pageSize);

  const hasParty = data.some((d) => d.code === 'party');
  const catalogCount = data.filter((d) => d.code !== 'party').length;

  const statusCell = (d: StateDepartmentRow) => {
    if (d.code === 'party') return <span className="text-xs font-semibold text-[#8a4b0f]">Draft</span>;
    if (d.coverage == null) return <span className="text-xs font-semibold text-brand">Ready</span>;
    return <span className={['text-xs font-semibold', covColor(d.coverage)].join(' ')}>{d.coverage}%</span>;
  };

  const subText = (d: StateDepartmentRow) =>
    d.customised ? 'Customised' : d.reviewed ? 'Same as template · reviewed' : 'Same as template';

  const columns: TableColumnsType<StateDepartmentRow> = [
    { title: '#', key: 'code', width: 56, render: (_, d) => <span className="text-xs text-ink-secondary">{d.code === 'party' ? '—' : d.code}</span> },
    {
      title: 'Hierarchy',
      key: 'name',
      render: (_, d) => {
        const party = d.code === 'party';
        const badge = party ? `${stateName} only` : d.customised ? 'Customised' : 'Template as-is';
        const badgeCls = party ? 'bg-brand text-white' : d.customised ? 'bg-[#fbeadb] text-[#73300b]' : 'bg-brand-shade text-brand';
        return (
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-ink">{d.name}</span>
            <span className={['w-fit rounded px-2 py-0.5 text-[11px] font-semibold', badgeCls].join(' ')}>{badge}</span>
            <span className="text-[11px] text-ink-secondary">{subText(d)}</span>
          </div>
        );
      },
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 90,
      render: (_, d) => (
        <span className={['rounded px-2 py-0.5 text-[11px] font-semibold', d.code === 'party' ? 'bg-brand text-white' : 'bg-muted-bg text-[#434655]'].join(' ')}>
          {d.code === 'party' ? 'Client' : 'State'}
        </span>
      ),
    },
    { title: 'Control', key: 'control', width: 130, render: (_, d) => <span className="text-xs text-[#434655]">{d.control}</span> },
    {
      title: 'Own levels',
      key: 'own',
      width: 190,
      render: (_, d) => {
        const binds = bindsOf(d.code);
        if (binds.length === 0) {
          return <span className="text-xs text-ink-secondary">{d.level_count} levels</span>;
        }
        const own = binds.filter(isOwn).length;
        return (
          <span className="flex items-center gap-2">
            <span className="flex gap-0.5" aria-hidden>
              {binds.map((b, i) => (
                <span key={i} className={['block size-[14px] rounded-[3px]', BIND_BLOCK[b]].join(' ')} />
              ))}
            </span>
            <span className="whitespace-nowrap text-xs text-ink-secondary">
              {own} of {binds.length}
            </span>
          </span>
        );
      },
    },
    { title: 'Bottom unit', key: 'bottom', width: 100, render: (_, d) => <span className="text-xs text-[#434655]">{d.bottom_unit}</span> },
    { title: 'Status', key: 'status', width: 110, render: (_, d) => statusCell(d) },
    {
      title: stateName,
      key: 'actions',
      width: 280,
      align: 'right',
      render: (_, d) => {
        const party = d.code === 'party';
        const stop = (fn: () => void) => (e: React.MouseEvent) => {
          e.stopPropagation();
          fn();
        };
        const useAsIs = () => {
          void updateStateDepartment({ code: stateCode, tcode: d.code, patch: { reviewed: true } })
            .unwrap()
            .catch(() => {});
        };
        const reset = () => {
          void updateStateDepartment({ code: stateCode, tcode: d.code, patch: { reset: true } })
            .unwrap()
            .catch(() => {});
        };
        return (
          <div className="flex flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap">
            {!party && d.customised && (
              <button
                type="button"
                onClick={stop(reset)}
                className="h-9 shrink-0 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-50"
              >
                Reset to template
              </button>
            )}
            {!party && !d.customised && !d.reviewed && (
              <>
                <button
                  type="button"
                  onClick={stop(useAsIs)}
                  className="h-9 shrink-0 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Use as-is
                </button>
                <button
                  type="button"
                  onClick={stop(() => open(d.code))}
                  className="h-9 shrink-0 rounded-lg border border-[#73300b] bg-white px-3 text-xs font-semibold text-[#73300b] transition-colors hover:bg-[#fdf4ec] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Customise
                </button>
              </>
            )}
            <button
              type="button"
              onClick={stop(() => open(d.code))}
              className="h-9 shrink-0 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
            >
              Open
            </button>
          </div>
        );
      },
    },
  ];

  const sourcePill = (s: string) => (
    <span key={s} className="inline-flex items-center gap-1.5 rounded-md bg-muted-bg px-3 py-1.5 text-[13px] font-semibold text-[#434655]">
      <span aria-hidden>🔒</span>
      {s}
    </span>
  );

  const SELECT_CLS = 'min-w-[150px]';

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: `Departments in ${stateName}` },
        ]}
      />
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div className="flex min-w-0 flex-1 basis-[520px] flex-col gap-2">
          <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Departments in {stateName}</h1>
          <p className="max-w-[820px] text-sm leading-6 text-ink-secondary">
            Each department is a template from the library plus the edits specific to {stateName}.
            Open one to change its structure, map its territories, record its people and see its
            minister.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-secondary">Sources, read-only</span>
          {['ECI', 'SEC', 'LGD'].map(sourcePill)}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-secondary">
        <span>Each block is one level, top to bottom:</span>
        {LEGEND.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5">
            <span className={['block size-[14px] rounded-[3px]', l.cls].join(' ')} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Search + filters toolbar */}
      <div className="rounded-xl border border-line/30 bg-white p-[17px] shadow-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px] flex-1">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search department by name or code..."
              className="w-full rounded-lg border border-line bg-white py-2.5 pl-10 pr-4 text-xs text-ink placeholder:text-[#9ca3af] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <Select
            className={SELECT_CLS}
            value={scope}
            onChange={onFilter(setScope)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'state', label: 'State' },
              { value: 'client', label: 'Client' },
            ]}
            labelRender={(p) => `Scope: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={status}
            onChange={onFilter(setStatus)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'ready', label: 'Ready' },
              { value: 'progress', label: 'In progress' },
              { value: 'draft', label: 'Draft' },
            ]}
            labelRender={(p) => `Status: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <Select
            className={SELECT_CLS}
            value={sort}
            onChange={onFilter(setSort)}
            options={[
              { value: 'catalog', label: 'Catalog order' },
              { value: 'work', label: 'Most to map first' },
            ]}
            labelRender={(p) => `Sort: ${p.label}`}
            suffixIcon={<CaretIcon className="size-3.5 text-[#6b7280]" />}
            popupMatchSelectWidth={false}
          />
          <button
            type="button"
            onClick={resetFilters}
            aria-label="Reset filters"
            className="rounded-lg border border-line bg-white p-2.5 text-[#6b7280] transition-colors hover:bg-brand-shade hover:text-brand"
          >
            <RefreshIcon className="size-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-2 px-[18px] py-3">
          <span className="text-[13px] text-ink-secondary">
            {rows.length} hierarchies · {catalogCount} of 25 catalog departments added{hasParty ? ', Party' : ''}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate('/admin/departments/add')}
              className="h-9 rounded-lg bg-brand px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add From Existing
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/departments/add/adhoc')}
              className="h-9 rounded-lg border border-brand bg-white px-3 text-xs font-semibold text-brand transition-colors hover:bg-brand-shade disabled:cursor-not-allowed disabled:opacity-50"
            >
              + Add New Department
            </button>
          </div>
        </div>
        {isLoading ? (
          <div className="px-[18px] py-6 text-sm text-ink-secondary">Loading departments…</div>
        ) : (
          <ConfigProvider theme={PLATFORM_TABLE_THEME}>
            <Table<StateDepartmentRow>
              className="chunaav-table"
              columns={columns}
              dataSource={paged}
              rowKey="code"
              pagination={false}
              onRow={(d) => ({ onClick: () => open(d.code) })}
              rowClassName="cursor-pointer"
            />
          </ConfigProvider>
        )}
        <Pagination
          rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
          rangeEnd={Math.min(page * pageSize, total)}
          total={total}
          noun="hierarchies"
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(v) => {
            setPageSize(v);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
